import { ReactElement } from 'react';

import IconPlayButtonBlackOutline from '@backrs_ui/common-ui/src/assets/images/play_button_black_outline.svg';
import { PanelNuggetActionParams } from '@backrs_ui/common-ui/src/models/nugget';

import { Box, SxProps, Theme } from '@mui/material';
import ReactPlayer from 'react-player';

import { BASE_NUGGET_IMAGE_MAX_HEIGHT } from './BaseNugget';
import { HoverButton } from './HoverButton';
import { NoPreviewHeader } from './NoPreviewHeader';

const YOUTUBE_REGEX =
  /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.be)(\/(?:[\w-]+\?v=|embed\/|v\/)?)([\w-]+)(\S+)?$/;

const TED_TALKS_REGEX = /^(https?:\/\/)?(www\.)?(ted\.com)(\/talks\/)([\w-]+)(\S+)?$/;

/**
 * Maps the media URL to the appropriate iframe source. As of now, only YouTube and TED Talks are supported, but other sources can be added in the future.
 */
function mediaUrliFrameMapper(url: string): string | undefined {
  const youtubeVideoId = url.match(YOUTUBE_REGEX)?.[5];

  if (youtubeVideoId) {
    return `https://www.youtube.com/embed/${youtubeVideoId}`;
  }

  const tedTalksVideoId = url.match(TED_TALKS_REGEX)?.[5];

  if (tedTalksVideoId) {
    return `https://embed.ted.com/talks/${tedTalksVideoId}`;
  }

  return undefined;
}

type NuggetProps = Readonly<{
  variant:
    | {
        type: 'SMALL';
        url: string;
        previewImage?: string;
        isInternal?: boolean;
        isHovered?: boolean;
        actionParams?: PanelNuggetActionParams;
      }
    | {
        type: 'LARGE';
        url: string;
      };
}>;

export const NuggetVideo = ({ variant }: NuggetProps): ReactElement => {
  if (variant.type === 'SMALL') {
    if (variant.isInternal) {
      // if the video is internal, we will use the video thumbnail as the preview image
      // we wont enable controls though
      return (
        <Box position="relative">
          <Box
            component="img"
            sx={playButtonContainerStyle}
            src={IconPlayButtonBlackOutline}
            width={56}
            height={56}
          />
          <Box sx={internalHoverButtonContainerStyle}>
            <HoverButton
              hasBookmarked={variant.actionParams?.hasBookmarked}
              handleActionBookmark={variant.actionParams?.handleActionBookmark}
              handleActionAddTask={variant.actionParams?.handleActionAddTask}
              handleActionShare={variant.actionParams?.handleActionShare}
              isVisible={variant.isHovered ?? false}
            />
          </Box>
          <Box sx={smallVideoReactPlayerStyle}>
            <ReactPlayer
              width="100%"
              height="100%"
              url={[{ src: variant.url, type: 'video/mp4' }]}
              config={{
                file: {
                  attributes: {
                    style: {
                      objectFit: 'cover',
                      width: '100%',
                      height: '100%',
                    },
                  },
                },
              }}
            />
          </Box>
        </Box>
      );
    }

    const previewImage = variant.previewImage || '';

    if (!previewImage) {
      return (
        <NoPreviewHeader
          variant={
            variant.isInternal
              ? {
                  type: 'video-internal',
                  url: variant.url,
                  previewImage: variant.previewImage,
                }
              : {
                  type: 'video-external',
                  url: variant.url,
                  previewImage: variant.previewImage,
                }
          }
          actionParams={variant.actionParams}
          isHovered={variant.isHovered ?? false}
        />
      );
    }

    return (
      <Box position="relative">
        <Box
          component="img"
          sx={playButtonContainerStyle}
          src={IconPlayButtonBlackOutline}
          width={56}
          height={56}
        />
        {variant.actionParams && (
          <HoverButton
            hasBookmarked={variant.actionParams?.hasBookmarked}
            handleActionBookmark={variant.actionParams?.handleActionBookmark}
            handleActionAddTask={variant.actionParams?.handleActionAddTask}
            handleActionShare={variant.actionParams?.handleActionShare}
            isVisible={variant.isHovered ?? false}
          />
        )}
        {variant.isInternal ? (
          <Box sx={videoThumbnailStyle(previewImage)}>
            {previewImage ? (
              <></>
            ) : (
              <video width="100%" height="100%" src={variant.url} style={{ objectFit: 'cover' }} />
            )}
          </Box>
        ) : (
          <Box sx={videoThumbnailStyle(previewImage)} />
        )}
      </Box>
    );
  }

  const dialogVideoIframeSource = mediaUrliFrameMapper(variant.url);

  return (
    <Box sx={videoPlayerStyle}>
      {dialogVideoIframeSource ? (
        <Box sx={externalIFrameContainerStyle}>
          <iframe
            width="100%"
            height="100%"
            src={dialogVideoIframeSource}
            title="Backrs video player"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </Box>
      ) : (
        <Box sx={largeInternalVideoContainerStyle}>
          <ReactPlayer
            controls
            width="100%"
            height="100%"
            url={[{ src: variant.url, type: 'video/mp4' }]}
          />
        </Box>
      )}
    </Box>
  );
};

const videoThumbnailStyle =
  (previewImage: string): SxProps<Theme> =>
  ({ backrs }) => ({
    width: '100%',
    aspectRatio: '16/9',
    maxHeight: BASE_NUGGET_IMAGE_MAX_HEIGHT,
    overflow: 'hidden',
    display: 'block',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    borderRadius: backrs.radius['radius-2xl'],
    border: `1px solid ${backrs.colors['Colors/Base/Black']}`,
    backgroundImage: `url(${previewImage})`,
  });

const externalIFrameContainerStyle: SxProps<Theme> = {
  aspectRatio: '16/9',
  borderRadius: ({ backrs }) => backrs.radius['radius-md'],
  border: '1px solid black',
  overflow: 'hidden',
};

const videoPlayerStyle: SxProps<Theme> = {
  width: '100%',
};

const playButtonContainerStyle: SxProps<Theme> = ({ backrs }) => ({
  position: 'absolute',
  bottom: backrs.spacing['spacing-lg'],
  left: backrs.spacing['spacing-2xl'],
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  zIndex: 1,
});

const smallVideoReactPlayerStyle: SxProps<Theme> = ({ backrs }) => ({
  width: '100%',
  aspectRatio: '16/9',
  maxHeight: BASE_NUGGET_IMAGE_MAX_HEIGHT,
  overflow: 'hidden',
  display: 'block',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  backgroundRepeat: 'no-repeat',
  borderRadius: backrs.radius['radius-2xl'],
  border: `1px solid ${backrs.colors['Colors/Base/Black']}`,
});

const internalHoverButtonContainerStyle: SxProps<Theme> = {
  position: 'absolute',
  zIndex: 2,
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
};

const largeInternalVideoContainerStyle: SxProps<Theme> = {
  borderRadius: ({ backrs }) => backrs.radius['radius-md'],
  border: '1px solid black',
  overflow: 'hidden',
  aspectRatio: '16/9',
};
